import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import Card from '../components/ui/Card'
import Button from '../components/ui/Button'
import LoadingState from '../components/ui/LoadingState'
import PageHeader from '../components/ui/PageHeader'
import PageShell from '../components/ui/PageShell'
import { ui } from '../styles/ui'
import queueService from '../services/queueService'
import { useTranslation } from '../contexts/TranslationContext'

// Maps queue config keys to their existing dedicated page routes.
const QUEUE_ROUTES = {
  receiptAnalysis: '/queue/receipt-analysis',
  productAnalysis: '/queue/product-analysis',
  productTranslation: '/queue/product-translation',
  qrcodeProductExtraction: '/queue/qrcode-product-extraction',
}

export default function QueueWorkers() {
  const [workers, setWorkers] = useState([])
  const [queues, setQueues] = useState([])
  const [loading, setLoading] = useState(false)
  const [queuesLoading, setQueuesLoading] = useState(false)
  const [queuesError, setQueuesError] = useState(false)
  const { t } = useTranslation()

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function load() {
    loadWorkers()
    loadQueues()
  }

  function applyWorkerStatus(status) {
    const workerData = status?.workers ?? status?.data ?? status
    if (Array.isArray(workerData)) {
      setWorkers(workerData.filter(Boolean))
      return
    }

    if (workerData && typeof workerData === 'object') {
      const ignoredWorkerKeys = new Set([
        'queue_processing',
        'queue_processing_enabled',
        'queueProcessingEnabled',
        'enabled',
        'workers',
        'data',
      ])
      setWorkers(
        Object.entries(workerData)
          .filter(([name]) => !ignoredWorkerKeys.has(name))
          .map(([name, data]) => (
            (data && typeof data === 'object')
              ? { __workerName: name, ...data }
              : { __workerName: name }
          ))
      )
      return
    }

    setWorkers([])
  }

  function loadWorkers() {
    setLoading(true)
    queueService.status()
      .then((res) => {
        const status = res?.data ?? res
        applyWorkerStatus(status)
      })
      .catch(() => setWorkers([]))
      .finally(() => setLoading(false))
  }

  function loadQueues() {
    setQueuesLoading(true)
    queueService.adminQueues()
      .then((res) => {
        setQueues(res.data?.data || [])
        setQueuesError(false)
      })
      .catch(() => { setQueues([]); setQueuesError(true) })
      .finally(() => setQueuesLoading(false))
  }

  const getStatusBadge = (count, variant, label) => {
    if (count == null) return <span className={`${ui.badge} ${ui.badgeNeutral}`}>{t('Unavailable')}</span>
    return <span aria-label={`${count} ${t(label)}`} className={`${ui.badge} ${count === 0 ? ui.badgeNeutral : variant}`}>{count}</span>
  }

  const transportRows = (queues || []).map((queue) => ({ ...queue, stats: queue.transport || {} }))
  const workflowRows = (queues || []).map((queue) => ({ ...queue, stats: queue.workflow || {} }))

  function renderMetricTable(rows, columns, group) {
    return (
      <div className={ui.card}>
        <div className="overflow-x-auto">
          <table className={ui.table}>
            <thead className={ui.tableHead}><tr>
              <th className={ui.tableHeadCell}>{t('Queue')}</th>
              {columns.map((column) => <th className={ui.tableHeadCell} key={column.key}>{t(column.label)}</th>)}
              <th className={ui.tableHeadCell}>{t('Details')}</th>
            </tr></thead>
            <tbody className="divide-y divide-divider">
              {rows.length === 0 ? (
                <tr><td colSpan={columns.length + 2} className="px-6 py-6 text-center text-sm text-muted-foreground">
                  {t('No queue data available')}
                </td></tr>
              ) : rows.map((row) => (
                <tr key={`${group}-${row.queueKey || row.key}`} className={ui.tableRow}>
                  <td className={ui.tableCell}>
                    <div className="text-sm font-medium text-foreground">{t(row.label)}</div>
                    <div className="text-xs text-muted-foreground">{row.name}</div>
                  </td>
                  {columns.map((column) => (
                    <td className={ui.tableCell} key={column.key}>
                      {group === 'transport' && row.stats.availability === 'unavailable'
                        ? t('Transport unavailable')
                        : group === 'transport' && row.stats.availability === 'disabled'
                          ? t('Transport disabled')
                          : row.stats[column.key] == null
                            ? (row.stats.applicable === false ? t('Not applicable') : t('Unavailable'))
                            : getStatusBadge(row.stats[column.key], column.variant || ui.badgeNeutral, column.label)}
                    </td>
                  ))}
                  <td className={ui.tableCell}>
                    <div className="text-xs text-muted-foreground">
                      {group === 'transport'
                        ? `${t('Source')}: ${row.stats.source || t('Unavailable')} · ${row.stats.observedAt || t('No observation')}`
                        : `${t('Stage DB state, not physical jobs')}${row.stats.terminalMismatchPending > 0 ? ` · ${row.stats.terminalMismatchPending} ${t('terminal mismatch')}` : ''}`}
                    </div>
                    {group === 'workflow' && QUEUE_ROUTES[row.key] && (
                      <Link to={QUEUE_ROUTES[row.key]} className={ui.buttonSmPrimary}>
                        {t(row.key === 'receiptAnalysis' ? 'View image receipt stage records' : 'View DB stage records')}
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  return (
    <PageShell>
      <PageHeader
        title={t('Workers Stats')}
        actions={<Button onClick={load}>{t('Refresh')}</Button>}
      />

      {loading && <LoadingState />}

      {!loading && (!workers || (Array.isArray(workers) ? workers.length === 0 : Object.keys(workers).length === 0)) && (
        <Card>
          <div>{t('No worker status available')}</div>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {workers && workers.map((data, index) => {
          const hasWorkerData = data && Object.keys(data).some((key) => key !== '__workerName')
          const workerName = data.__workerName ?? data.worker ?? data.name ?? `worker-${index}`

          return (
            <Card key={workerName || index}>
              <h3 className="text-lg font-semibold">{workerName}</h3>
              {hasWorkerData ? (
                <div className="mt-2 space-y-1 text-sm text-foreground">
                  <div>PID: {data.pid ?? '-'}</div>
                  <div>Last heartbeat: {data.last_heartbeat ?? '-'}</div>
                  <div>Processed: {data.processed ?? 0}</div>
                  <div>Worker: {data.worker ?? '-'}</div>
                </div>
              ) : (
                <div className="mt-2 text-sm text-muted-foreground">{t('No data')}</div>
              )}
            </Card>
          )
        })}
      </div>

      <div className="space-y-5">
        <section aria-labelledby="queue-transport-heading">
          <h2 id="queue-transport-heading" className="mb-2 text-lg font-semibold text-foreground">{t('BullMQ / execution transport')}</h2>
          <p className="mb-3 text-sm text-muted-foreground">{t('Physical job counters are separate from PostgreSQL receipt-stage records.')}</p>
          {queuesError && <p role="alert" className="mb-3 text-sm text-destructive">{t('Queue metrics could not be loaded. Refresh to retry.')}</p>}
          {queuesLoading ? <LoadingState /> : renderMetricTable(transportRows, [
            { key: 'waiting', label: 'Waiting', variant: ui.badgeWarning }, { key: 'active', label: 'Active', variant: ui.badgeWarning },
            { key: 'delayed', label: 'Delayed', variant: ui.badgeWarning }, { key: 'failed', label: 'Failed', variant: ui.badgeError },
          ], 'transport')}
        </section>
        <section aria-labelledby="queue-workflow-heading">
          <h2 id="queue-workflow-heading" className="mb-2 text-lg font-semibold text-foreground">{t('PostgreSQL workflow')}</h2>
          <p className="mb-3 text-sm text-muted-foreground">{t('Pending means an explicitly stored Receipt stage. Blocked stages are not runnable jobs; a failed predecessor or terminal receipt may explain them.')}</p>
          {queuesLoading ? <LoadingState /> : renderMetricTable(workflowRows, [
            { key: 'rawPending', label: 'Raw pending', variant: ui.badgeWarning }, { key: 'runnablePending', label: 'Runnable', variant: ui.badgeSuccess },
            { key: 'blockedPending', label: 'Blocked', variant: ui.badgeError }, { key: 'unknownPending', label: 'Unknown' },
            { key: 'staleProcessing', label: 'Stale processing', variant: ui.badgeWarning },
            { key: 'verifiedOrphanProcessing', label: 'Verified orphan', variant: ui.badgeError },
          ], 'workflow')}
        </section>
      </div>
    </PageShell>
  )
}

